import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import {
  differenceInDays,
  endOfDay,
  format,
  isAfter,
  startOfDay,
  startOfMonth,
  subDays,
  subYears,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { DateRange } from "react-day-picker";
import { BarChart3, CalendarIcon, ChevronDown, RefreshCw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useDealsDashboardStats, type DealsStatsDateRange } from "@/hooks/useDealsDashboardStats";

const DealsPipelineByStageChart = lazy(() => import("@/components/deals/charts/DealsPipelineByStageChart"));
const DealsForecastChart = lazy(() => import("@/components/deals/charts/DealsForecastChart"));
const DealsMemberPipelineChart = lazy(() => import("@/components/deals/charts/DealsMemberPipelineChart"));

function ChartSlot({ className }: { className?: string }) {
  return (
    <div className={cn("h-[280px] flex items-center justify-center rounded-lg border bg-muted/10", className)}>
      <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
    </div>
  );
}

function formatBRL(v: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);
}

function formatAvgDays(d: number | null) {
  if (d === null) return "—";
  return `${d.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 1 })} dias`;
}

const MAX_RANGE_DAYS = 366;
const MAX_HISTORY_YEARS = 5;

type DealsStatsPreset = "month" | "30d" | "custom";

function rollingPresetRange(preset: "month" | "30d", now = new Date()): DealsStatsDateRange {
  if (preset === "month") {
    return { from: startOfMonth(now), to: now };
  }
  return { from: startOfDay(subDays(now, 29)), to: now };
}

function initialRange(): DealsStatsDateRange {
  return rollingPresetRange("month");
}

function minSelectableDate(): Date {
  return startOfDay(subYears(new Date(), MAX_HISTORY_YEARS));
}

function normalizeCustomRange(from: Date, to: Date): DealsStatsDateRange {
  const fromNorm = startOfDay(from);
  const today = new Date();
  const toNorm =
    startOfDay(to).getTime() === startOfDay(today).getTime() ? today : endOfDay(to);
  return { from: fromNorm, to: toNorm };
}

function validateCustomRange(from: Date, to: Date): string | null {
  const fromDay = startOfDay(from);
  const toDay = startOfDay(to);
  const today = startOfDay(new Date());

  if (isAfter(fromDay, toDay)) {
    return "A data final deve ser igual ou posterior à data inicial.";
  }
  if (fromDay < minSelectableDate()) {
    return `A data inicial não pode ser anterior a ${MAX_HISTORY_YEARS} anos.`;
  }
  if (isAfter(toDay, today)) {
    return "A data final não pode ser no futuro.";
  }
  if (differenceInDays(toDay, fromDay) > MAX_RANGE_DAYS) {
    return `O período máximo é de ${MAX_RANGE_DAYS} dias.`;
  }
  return null;
}

function formatRangeLabel(from: Date, to: Date): string {
  return `${format(from, "dd/MM/yyyy", { locale: ptBR })} – ${format(to, "dd/MM/yyyy", { locale: ptBR })}`;
}

interface DealsInsightsPanelProps {
  workspaceId: string;
}

export function DealsInsightsPanel({ workspaceId }: DealsInsightsPanelProps) {
  const { toast } = useToast();
  const [range, setRange] = useState<DealsStatsDateRange>(initialRange);
  const [open, setOpen] = useState(false);
  const [activePreset, setActivePreset] = useState<DealsStatsPreset>("month");
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>();

  const { data, isPending, isError, error, refetch, isFetching } = useDealsDashboardStats(
    workspaceId,
    range,
    open,
  );

  const refreshRollingRange = useCallback((preset: DealsStatsPreset = activePreset) => {
    if (preset !== "month" && preset !== "30d") return;
    setRange(rollingPresetRange(preset));
  }, [activePreset]);

  useEffect(() => {
    if (!open) return;
    refreshRollingRange();
  }, [open, refreshRollingRange]);

  const applyPreset = useCallback((preset: "month" | "30d") => {
    setActivePreset(preset);
    setRange(rollingPresetRange(preset));
  }, []);

  const applyCustomRange = useCallback(
    (pickerRange: DateRange | undefined) => {
      setCustomDateRange(pickerRange);
      if (!pickerRange?.from || !pickerRange?.to) return;

      const validationError = validateCustomRange(pickerRange.from, pickerRange.to);
      if (validationError) {
        toast({
          title: "Período inválido",
          description: validationError,
          variant: "destructive",
        });
        return;
      }

      setActivePreset("custom");
      setRange(normalizeCustomRange(pickerRange.from, pickerRange.to));
    },
    [toast],
  );

  const stats = data;

  const handleRefresh = useCallback(() => {
    refreshRollingRange();
    if (activePreset === "custom") {
      void refetch();
    }
  }, [activePreset, refreshRollingRange, refetch]);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-xl border bg-card shadow-sm">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 p-4 text-left hover:bg-muted/40 rounded-t-xl transition-colors"
        >
          <div className="flex items-center gap-2 min-w-0">
            <BarChart3 className="h-5 w-5 text-primary shrink-0" />
            <div className="min-w-0">
              <p className="font-semibold text-sm sm:text-base">Métricas do período</p>
              <p className="text-xs text-muted-foreground truncate">
                Carrega ao abrir · kanban abaixo em tempo real
              </p>
            </div>

          </div>
          <ChevronDown className={cn("h-5 w-5 text-muted-foreground shrink-0 transition-transform", open && "rotate-180")} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 pb-4 space-y-4 border-t pt-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 flex-wrap">
            <div className="flex flex-col gap-1.5 min-w-0">
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant={activePreset === "month" ? "secondary" : "outline"}
                  size="sm"
                  onClick={() => applyPreset("month")}
                >
                  Este mês
                </Button>
                <Button
                  variant={activePreset === "30d" ? "secondary" : "outline"}
                  size="sm"
                  onClick={() => applyPreset("30d")}
                >
                  Últimos 30 dias
                </Button>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={activePreset === "custom" ? "secondary" : "outline"}
                      size="sm"
                    >
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      Personalizado
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      selected={customDateRange}
                      onSelect={applyCustomRange}
                      initialFocus
                      className="pointer-events-auto"
                      locale={ptBR}
                      disabled={(date) => {
                        const day = startOfDay(date);
                        return day > startOfDay(new Date()) || day < minSelectableDate();
                      }}
                    />
                    <p className="px-3 pb-3 text-[11px] text-muted-foreground">
                      Máximo de {MAX_RANGE_DAYS} dias · até {MAX_HISTORY_YEARS} anos atrás
                    </p>
                  </PopoverContent>
                </Popover>
              </div>
              {activePreset === "custom" && (
                <p className="text-xs text-muted-foreground truncate">
                  {formatRangeLabel(range.from, range.to)}
                </p>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="sm:ml-auto"
              onClick={handleRefresh}
              disabled={isFetching}
            >
              <RefreshCw className={cn("h-4 w-4 mr-1", isFetching && "animate-spin")} />
              Atualizar métricas
            </Button>
          </div>

          {isError && (
            <Card className="border-destructive/50 bg-destructive/5">
              <CardContent className="pt-4 text-sm text-destructive">
                <p className="font-medium">Não foi possível carregar as métricas.</p>
                <p className="text-destructive/90 mt-1">{error instanceof Error ? error.message : "Erro desconhecido"}</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => void refetch()}>
                  Tentar novamente
                </Button>
              </CardContent>
            </Card>
          )}

          {open && isPending && !isError && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="h-24 rounded-lg bg-muted/50 animate-pulse" />
              ))}
            </div>
          )}

          {stats && !isError && !isPending && (
            <>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-xs text-muted-foreground">Pipeline aberto (bruto)</p>
                    <p className="text-xl font-bold tabular-nums">{formatBRL(stats.pipeline_total)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-xs text-muted-foreground">Pipeline ponderado</p>
                    <p className="text-xl font-bold tabular-nums">{formatBRL(stats.pipeline_weighted)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-xs text-muted-foreground">Ganhos no período</p>
                    <p className="text-xl font-bold tabular-nums text-green-600 dark:text-green-400">
                      {stats.won_count} · {formatBRL(stats.won_total)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-xs text-muted-foreground">Perdidos no período</p>
                    <p className="text-xl font-bold tabular-nums text-red-600 dark:text-red-400">
                      {stats.lost_count} · {formatBRL(stats.lost_total)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-xs text-muted-foreground">Ticket médio (ganhos)</p>
                    <p className="text-xl font-bold tabular-nums">{formatBRL(stats.avg_ticket)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-xs text-muted-foreground">Tempo médio até fechar</p>
                    <p className="text-xl font-bold tabular-nums">{formatAvgDays(stats.avg_days_to_close_all)}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Negócios ganhos ou perdidos com data de fechamento no período (desde a criação).
                    </p>
                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                      <span>
                        Ganhos: <span className="tabular-nums text-foreground">{formatAvgDays(stats.avg_days_to_close_won)}</span>
                      </span>
                      <span>
                        Perdidos: <span className="tabular-nums text-foreground">{formatAvgDays(stats.avg_days_to_close_lost)}</span>
                      </span>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-xs text-muted-foreground">Leads → negociação (período)</p>
                    <p className="text-xl font-bold tabular-nums">{stats.lead_to_deal_rate.toFixed(1)}%</p>
                    <p className="text-[10px] text-muted-foreground mt-1">Novos leads no período que geraram negociação</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid gap-6 lg:grid-cols-2">
                <Suspense fallback={<ChartSlot />}>
                  <DealsPipelineByStageChart byStage={stats.by_stage} />
                </Suspense>
                <Suspense fallback={<ChartSlot />}>
                  <DealsForecastChart forecast={stats.forecast_by_month} />
                </Suspense>
              </div>
              <Suspense fallback={<ChartSlot className="h-[240px]" />}>
                <DealsMemberPipelineChart byMember={stats.by_member} />
              </Suspense>
            </>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
